import React, { useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    FloatingFocusManager, FloatingPortal, autoUpdate, flip, offset, safePolygon, shift,
    useDismiss, useFloating, useHover, useInteractions,
} from '@floating-ui/react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import KitButton from '../../components/ui/Button';
import { floatingPaperClassName } from '../../components/ui/Popover';
import { controlResetClassName, cx } from '../../components/ui/classNames';
import CalendarCard from './CalendarCard';
import { fetchMarketCalendar } from './exploreApi';
import { leadInStart } from './exploreEnvironment';
import { formatDay, formatWindow } from './exploreFormat';

const INFO_BUTTON_CLASS_NAME = cx(
    controlResetClassName,
    'inline-flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0',
    'text-ui-muted hover:bg-ui-hover hover:text-ui-ink focus:outline-none focus-visible:outline focus-visible:outline-2',
    'focus-visible:outline-ui-focus',
);
const CARD_CLASS_NAME = 'grid w-[min(360px,calc(100vw-32px))] max-h-[min(560px,80vh)] gap-2 overflow-y-auto p-3 text-[12px]';
const KIND_LABELS = Object.freeze({ standard: 'Standard library', yours: 'Your event' });

// What the market calendar holds for the event's window and the month before it.
function MarketCalendar({ column }) {
    const from = leadInStart(column.start_date);
    const calendar = useQuery({
        queryKey: ['market_calendar', from, column.end_date],
        queryFn: ({ signal }) => fetchMarketCalendar(from, column.end_date, { signal }),
        staleTime: 600_000,
        gcTime: 600_000,
        retry: false,
        refetchOnWindowFocus: false,
    });
    return (
        <CalendarCard windowLabel={`${formatWindow(from, column.end_date)}, from the month before`} leadInBefore={column.start_date}
            loading={calendar.isLoading} error={calendar.error} calendar={calendar.data || null} onRetry={calendar.refetch} />
    );
}

MarketCalendar.propTypes = {
    column: PropTypes.shape({ start_date: PropTypes.string.isRequired, end_date: PropTypes.string.isRequired }).isRequired,
};

// The card's actions: copy a standard event, save a found one, or edit or
// delete one of yours. Deleting asks first, with focus on Keep; Keep returns
// focus to Delete.
function CardActions({ column, onAction }) {
    const [confirming, setConfirming] = useState(false);
    const [kept, setKept] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState(null);
    if (column.tag === 'standard') return <KitButton onClick={() => onAction('copy')} className="justify-self-start">Copy to Your events</KitButton>;
    if (column.tag === 'found') return <KitButton onClick={() => onAction('save')} className="justify-self-start">Save to Your events</KitButton>;
    const remove = async () => {
        setDeleting(true);
        setError(null);
        try {
            await onAction('delete');
        } catch (failure) {
            setError(failure?.message || 'The event couldn’t be deleted.');
            setDeleting(false);
        }
    };
    if (confirming) {
        return (
            <div role="group" aria-label={`Delete ${column.name}`} className="grid gap-1.5 rounded-md border border-solid border-ui-danger-line bg-ui-danger-bg p-2">
                <span>Delete “{column.name}”? Its column leaves the table, and this can’t be undone.</span>
                {error && <span role="alert" className="text-ui-danger">{error}</span>}
                <div className="flex justify-end gap-1">
                    <KitButton autoFocus onClick={() => { setConfirming(false); setKept(true); }} disabled={deleting}>Keep</KitButton>
                    <KitButton onClick={remove} disabled={deleting} className="!text-ui-danger">{deleting ? 'Deleting…' : 'Delete event'}</KitButton>
                </div>
            </div>
        );
    }
    return (
        <div className="flex gap-1">
            <KitButton onClick={() => onAction('edit')}>Edit</KitButton>
            <KitButton autoFocus={kept} onClick={() => setConfirming(true)}>Delete</KitButton>
        </div>
    );
}

CardActions.propTypes = {
    column: PropTypes.shape({ name: PropTypes.string.isRequired, tag: PropTypes.string.isRequired }).isRequired,
    onAction: PropTypes.func.isRequired,
};

/**
 * What an event is: its kind, title and dates, the description (standard)
 * or your notes, and on request what happened in the market then, read from
 * the stored market calendar.
 */
function EventCardBody({ column, titleId, baselineLabel, onAction }) {
    const [market, setMarket] = useState(false);
    const kind = column.tag === 'found' ? `Found for ${baselineLabel}` : KIND_LABELS[column.tag];
    let text = column.tag === 'standard' ? column.description : column.notes;
    if (column.tag === 'found') {
        // "Best month #1" names a ranked window of trading days, not a calendar month.
        const outcome = column.side === 'best' ? 'gain' : 'loss';
        text = `Found by scanning ${baselineLabel}’s factor replay over the whole history, so it changes with the baseline. `
            + `Each window is ${column.horizonDays} trading days: #1 is the largest ${outcome}, #2 the next, and none overlap.`;
    }
    const Chevron = market ? ChevronDown : ChevronRight;
    return (
        <>
            <div className="grid gap-0.5">
                <span className="text-[10.5px] font-bold uppercase tracking-normal text-ui-muted">{kind}</span>
                <h4 id={titleId} className="m-0 text-[13.5px] font-semibold leading-[1.3] text-content-primary">{column.name}</h4>
                <span className="text-[11.5px] text-content-secondary">
                    {column.start_date === column.end_date ? formatDay(column.start_date) : formatWindow(column.start_date, column.end_date)}
                </span>
            </div>
            {text && <p className="m-0 whitespace-pre-line leading-[1.45] text-content-primary">{text}</p>}
            <KitButton size="sm" aria-expanded={market} onClick={() => setMarket(!market)} className="justify-self-start gap-0.5">
                <Chevron aria-hidden="true" size={14} strokeWidth={2.25} />
                What happened in the market
            </KitButton>
            {market && <MarketCalendar column={column} />}
            {onAction && <CardActions column={column} onAction={onAction} />}
        </>
    );
}

EventCardBody.propTypes = {
    column: PropTypes.shape({
        tag: PropTypes.oneOf(['standard', 'yours', 'found']).isRequired, name: PropTypes.string.isRequired,
        start_date: PropTypes.string.isRequired, end_date: PropTypes.string.isRequired,
        description: PropTypes.string, notes: PropTypes.string, side: PropTypes.oneOf(['worst', 'best']), horizonDays: PropTypes.number,
    }).isRequired,
    titleId: PropTypes.string.isRequired,
    baselineLabel: PropTypes.string.isRequired,
    onAction: PropTypes.func,
};

/**
 * An event's chip or column header (`children`) with the event's card. The
 * card peeks while the pointer rests on it, and the info button after it
 * pins the card open; so does a click inside a peeking card. Phones have no
 * hover, so the info button is their way in, and keyboard users reach it by
 * Tab. A pinned card is a non-modal dialog: focus moves into it, and Escape
 * or a click outside closes it, returning focus to the info button.
 * `onAction(action, column, anchor)` receives 'copy', 'save', 'edit' or
 * 'delete' (which may reject with a message) and the chip or header element.
 * The first three open the event pop-out once the card has closed and handed
 * focus back, so the pop-out's own focus (and its return on close) holds.
 */
export default function EventCardAnchor({ column, baselineLabel, onAction = null, className, infoClassName, children }) {
    const [mode, setMode] = useState(null);
    const infoRef = useRef(null);
    const titleId = useId();
    const { refs, floatingStyles, context } = useFloating({
        open: mode !== null,
        onOpenChange: (open, _event, reason) => {
            if (reason === 'hover' && mode === 'pinned') return;
            setMode(open ? 'peek' : null);
        },
        placement: 'bottom-start',
        strategy: 'fixed',
        whileElementsMounted: autoUpdate,
        middleware: [offset(6), flip(), shift({ padding: 8 })],
    });
    const hover = useHover(context, { move: false, delay: { open: 350, close: 120 }, handleClose: safePolygon() });
    const dismiss = useDismiss(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss]);
    const act = onAction && ((action) => {
        const anchor = refs.domReference.current;
        if (action === 'delete') return onAction(action, column, anchor);
        setMode(null);
        setTimeout(() => onAction(action, column, anchor), 0);
        return undefined;
    });
    return (
        <span ref={refs.setReference} className={className} {...getReferenceProps()}>
            {children}
            <button ref={infoRef} type="button" className={cx(INFO_BUTTON_CLASS_NAME, infoClassName)} aria-label={`About ${column.name}`}
                aria-haspopup="dialog" aria-expanded={mode === 'pinned'} onClick={() => setMode(mode === 'pinned' ? null : 'pinned')}>
                <Info aria-hidden="true" size={13} strokeWidth={2.25} />
            </button>
            {mode !== null && (
                <FloatingPortal>
                    <FloatingFocusManager context={context} modal={false} disabled={mode !== 'pinned'} returnFocus={infoRef}>
                        <div ref={refs.setFloating} role="dialog" aria-labelledby={titleId} style={floatingStyles}
                            className={cx(floatingPaperClassName, CARD_CLASS_NAME)}
                            {...getFloatingProps({ onPointerDown: () => setMode('pinned') })}>
                            <EventCardBody column={column} titleId={titleId} baselineLabel={baselineLabel} onAction={act} />
                        </div>
                    </FloatingFocusManager>
                </FloatingPortal>
            )}
        </span>
    );
}

EventCardAnchor.propTypes = {
    column: PropTypes.shape({ name: PropTypes.string.isRequired }).isRequired,
    baselineLabel: PropTypes.string.isRequired,
    onAction: PropTypes.func,
    className: PropTypes.string,
    infoClassName: PropTypes.string,
    children: PropTypes.node.isRequired,
};
